import React from "react"
import { Spacer } from "gatsby-interface"
import { siteDetails as text } from "@modules/locales/default.json"
import MiniReport from "../../metrics/components/MiniReport"
import {
  isBuildRunnerType,
  successfulBuild,
  isIncrementalUpdate,
} from "../../shared/utils"
import useSiteCdnIntegrations from "../../../site/cdnIntegrations/hooks/useSiteCdnIntegrations"
import { BuildStatus, SubscriptionStatus } from "@modules/graphql/types"
import DateTime from "@modules/ui/components/DateTime"
import BuildViewLink from "@modules/build/shared/components/BuildViewLink"
import { BuildEventText } from "@modules/build/shared/components/BuildEventText"
import BuildPreviewLink from "./PreviewLink"
import { DateTimeDistanceToNow } from "@modules/ui/components/DateTimeDistance"
import IncrementalBuildChip from "@modules/build/shared/components/IncrementalBuildChip"
import BuildStatusIndicator from "@modules/build/shared/components/BuildStatusIndicator"
import BuildCardStatusInfo from "@modules/build/card/components/BuildCardStatusInfo"
import { DeploymentTiming } from "@modules/build/card/components/DeploymentTiming"
import { DeploymentStatus } from "@modules/build/card/components/DeploymentStatus"
import BuildCommitInfo from "@modules/build/shared/components/BuildCommitInfo"
import BuildAuthor from "@modules/build/card/components/BuildAuthor"
import {
  BuildDetailsInfoLayout,
  BuildDetailsStatusIconSlot,
  BuildDetailsStatusTextSlot,
  BuildDetailsBuildUrlSlot,
  BuildDetailsDeployIconSlot,
  BuildDetailsDeployTextSlot,
  BuildDetailsCdnVendorSlot,
  BuildDetailsStartTimeSlot,
  BuildDetailsEventInfoSlot,
  BuildDetailsLogsSlot,
} from "./BuildDetailsInfo.layout"
import { CdnIntegrationChip } from "@modules/cdn/shared/components/CdnIntegrationChip"
import { StructuredLogsTabs } from "@modules/build/logs/structured/components/StructuredLogsTabs"
import { isProperlyConnected } from "@modules/site/cdnIntegrations"

export function BuildDetailsInfo({
  buildStatus,
  previewUrl,
  duration,
  createdAt,
  startedAt,
  endedAt,
  createdAtDate,
  deployStartedAt,
  deployEndedAt,
  structuredBuild,
  commit,
  author,
  branch,
  buildType,
  runnerType,
  buildId,
  siteId,
  organizationId,
  repositoryUrl,
  source,
  cdnVendor,
}) {
  const cdnIntegrations = useSiteCdnIntegrations(siteId, branch)
  const mainIntegration = cdnIntegrations.find(isProperlyConnected)
  const isBuildDetails = isBuildRunnerType(runnerType)
  const showDeploymentStatus = isBuildDetails && deployStartedAt
  const showCdnIntegrations =
    showDeploymentStatus && Boolean(mainIntegration) && isBuildDetails
  const showStructuredLogs =
    structuredBuild && buildStatus !== SubscriptionStatus.Canceled

  return (
    <div
      data-cy="build-details"
      data-buildid={buildId}
      css={theme => [
        theme.cardStyles.frame,
        theme.cardStyles.space.L,
        { marginBottom: theme.space[5] },
      ]}
    >
      <BuildDetailsInfoLayout>
        <BuildDetailsBuildUrlSlot>
          {isBuildDetails ? (
            <BuildViewLink
              buildId={buildId}
              buildStatus={buildStatus}
              uiSource="Build Details"
              organizationId={organizationId}
            />
          ) : (
            buildStatus === BuildStatus.Success && (
              <BuildPreviewLink previewUrl={previewUrl} />
            )
          )}
        </BuildDetailsBuildUrlSlot>
        <BuildDetailsStatusIconSlot>
          <BuildStatusIndicator
            a11yId={buildId}
            buildStatus={buildStatus}
            runnerType={runnerType}
            branch={branch}
          />
        </BuildDetailsStatusIconSlot>
        <BuildDetailsStatusTextSlot data-buildid={buildId}>
          <BuildCardStatusInfo
            createdAt={createdAt}
            buildStatus={buildStatus}
            startedAt={startedAt}
            endedAt={endedAt}
            duration={duration}
          />
        </BuildDetailsStatusTextSlot>
        {showDeploymentStatus && (
          <React.Fragment>
            <BuildDetailsDeployIconSlot>
              <DeploymentStatus value={buildStatus} />
            </BuildDetailsDeployIconSlot>
            <BuildDetailsDeployTextSlot>
              <DeploymentTiming
                status={buildStatus}
                startDate={new Date(deployStartedAt)}
                endDate={deployEndedAt ? new Date(deployEndedAt) : undefined}
                vendor={mainIntegration?.vendor || ""}
                showVendorName={false}
              />
            </BuildDetailsDeployTextSlot>
          </React.Fragment>
        )}
        {showCdnIntegrations && Boolean(cdnVendor) && (
          <BuildDetailsCdnVendorSlot>
            <CdnIntegrationChip
              vendor={cdnVendor}
              siteId={siteId}
              organizationId={organizationId}
            />
          </BuildDetailsCdnVendorSlot>
        )}
        <BuildDetailsEventInfoSlot>
          <div
            css={theme => ({
              display: `flex`,
              flexWrap: `wrap`,
              marginBottom: `-${theme.space[3]}`,
            })}
          >
            <BuildAuthor
              buildType={buildType}
              commit={commit}
              author={author}
              source={source}
            />
            <Spacer size={3} direction="horizontal" />
            <BuildCommitInfo
              branch={branch}
              commit={commit}
              repositoryUrl={repositoryUrl || ""}
              withLink
            />
            <Spacer size={3} direction="horizontal" />
            {isIncrementalUpdate(runnerType, buildType) && (
              <IncrementalBuildChip />
            )}
          </div>
          <Spacer size={2} />
          <BuildEventText
            commit={commit}
            buildType={buildType}
            buildSource={source}
            runnerType={runnerType}
            collapsible
          />
        </BuildDetailsEventInfoSlot>
        <BuildDetailsStartTimeSlot>
          <time dateTime={createdAt} data-cy="buildDetails__time">
            {text.created}
            {` `}
            <DateTimeDistanceToNow fromDate={createdAtDate} />
          </time>
          <Spacer size={0} responsiveSize={{ tablet: 3 }} />
          <DateTime
            date={createdAtDate}
            css={theme => ({
              color: theme.colors.grey[60],
              fontSize: theme.fontSizes[1],
            })}
          />
        </BuildDetailsStartTimeSlot>
        {showStructuredLogs && (
          <BuildDetailsLogsSlot>
            <StructuredLogsTabs
              buildId={buildId}
              siteId={siteId}
              organizationId={organizationId}
              runnerType={runnerType}
              buildStatus={buildStatus}
            />
          </BuildDetailsLogsSlot>
        )}

        {isBuildDetails && successfulBuild(buildStatus) && (
          <div css={{ gridArea: `lighthouse` }}>
            <MiniReport siteId={siteId} buildId={buildId} branch={branch} />
          </div>
        )}
      </BuildDetailsInfoLayout>
    </div>
  )
}
